// @flow

import Admin from '../entities/models/Admin';
import AdminRT from '../entities/models/AdminRT';
import Beacon from '../entities/models/Beacon';
import type { Mode } from './modes';
import LocationSession from '../entities/models/LocationSession';
import ContactRequest from '../entities/models/ContactRequest';
import Organization from '../entities/models/Organization';
import OrganizationUser from '../entities/models/OrganizationUser';
import OrgLocation from '../entities/models/OrgLocation';
import OrgUserRT from '../entities/models/OrgUserRT';
import PaymentMethod from '../entities/models/PaymentMethod';
import PhoneCheck from '../entities/models/PhoneCheck';
import Policy from '../entities/models/Policy';
import RewardAccount from '../entities/models/RewardAccount';
import RewardTransaction from '../entities/models/RewardTransaction';
import Transaction from '../entities/models/Transaction';
import User from '../entities/models/User';
import PolicyEntityAction from '../entities/models/PolicyEntityAction';
import Bill from '../entities/models/Bill';
import AuthSession from '../entities/models/AuthSession';
import OrgReview from '../entities/models/OrgReview';

//
//
// ENTITY ACTIONS
//
//

export type RegistryEntityAction = {
    list: Mode,
    info: Mode,
    create: Mode,
    edit: Mode,
    [key: string]: Mode,
};

export type RegistryEntityActionCollection = {
    [key: string]: RegistryEntityAction,
};

// Generate an allow-all (except nonexistent actions) registry record for all known entities
export function getAllRegistryEntityActions(): RegistryEntityActionCollection {
    return {
        [Admin.classInternalName]: {
            operations: {
                c: undefined,
                r: true,
                u: true,
                d: undefined,
            },
        },
        [AdminRT.classInternalName]: {
            operations: {
                c: true,
                r: true,
                u: true,
                d: undefined,
            },
        },
        [Beacon.classInternalName]: {
            operations: {
                c: true,
                r: true,
                u: true,
                d: undefined,
            },
        },
        [ContactRequest.classInternalName]: {
            operations: {
                c: undefined,
                r: true,
                u: true,
                d: undefined,
            },
        },
        [Bill.classInternalName]: {
            operations: {
                c: undefined,
                r: true,
                u: undefined,
                d: true,
            },
        },
        [LocationSession.classInternalName]: {
            operations: {
                c: undefined,
                r: true,
                u: undefined,
                d: true,
            },
        },
        [Organization.classInternalName]: {
            operations: {
                c: true,
                r: true,
                u: true,
                d: undefined,
            },
        },
        [OrganizationUser.classInternalName]: {
            operations: {
                c: undefined,
                r: true,
                u: true,
                d: undefined,
            },
        },
        [OrgLocation.classInternalName]: {
            operations: {
                c: true,
                r: true,
                u: true,
                d: undefined,
            },
        },
        [OrgUserRT.classInternalName]: {
            operations: {
                c: true,
                r: true,
                u: true,
                d: undefined,
            },
        },
        [PaymentMethod.classInternalName]: {
            operations: {
                c: undefined,
                r: true,
                u: undefined,
                d: undefined,
            },
        },
        [PhoneCheck.classInternalName]: {
            operations: {
                c: undefined,
                r: true,
                u: undefined,
                d: undefined,
            },
        },
        [Policy.classInternalName]: {
            operations: {
                c: true,
                r: true,
                u: true,
                d: undefined,
            },
        },
        [RewardAccount.classInternalName]: {
            operations: {
                c: undefined,
                r: true,
                u: true,
                d: undefined,
            },
        },
        [RewardTransaction.classInternalName]: {
            operations: {
                c: true,
                r: true,
                u: undefined,
                d: undefined,
            },
        },
        [Transaction.classInternalName]: {
            operations: {
                c: undefined,
                r: true,
                u: undefined,
                d: undefined,
            },
        },
        [User.classInternalName]: {
            operations: {
                c: undefined,
                r: true,
                u: true,
                d: true,
            },
        },
        [AuthSession.classInternalName]: {
            operations: {
                c: undefined,
                r: true,
                u: true,
                d: undefined,
            },
        },
        [OrgReview.classInternalName]: {
            operations: {
                c: undefined,
                r: true,
                u: true,
                d: undefined,
            },
        },
    };
}

// Generate a deny-all (except nonexistent actions) registry record for all known entities
export function getAllDeniedEntityActions(): RegistryEntityActionCollection {
    return {
        [Admin.classInternalName]: {
            operations: {
                c: undefined,
                r: false,
                u: false,
                d: undefined,
            },
        },
        [AdminRT.classInternalName]: {
            operations: {
                c: false,
                r: false,
                u: false,
                d: undefined,
            },
        },
        [Beacon.classInternalName]: {
            operations: {
                c: false,
                r: false,
                u: false,
                d: undefined,
            },
        },
        [ContactRequest.classInternalName]: {
            operations: {
                c: undefined,
                r: false,
                u: false,
                d: undefined,
            },
        },
        [Bill.classInternalName]: {
            operations: {
                c: undefined,
                r: false,
                u: undefined,
                d: undefined,
            },
        },
        [LocationSession.classInternalName]: {
            operations: {
                c: undefined,
                r: false,
                u: undefined,
                d: undefined,
            },
        },
        [Organization.classInternalName]: {
            operations: {
                c: false,
                r: false,
                u: false,
                d: undefined,
            },
        },
        [OrganizationUser.classInternalName]: {
            operations: {
                c: undefined,
                r: false,
                u: false,
                d: undefined,
            },
        },
        [OrgLocation.classInternalName]: {
            operations: {
                c: false,
                r: false,
                u: false,
                d: undefined,
            },
        },
        [OrgUserRT.classInternalName]: {
            operations: {
                c: false,
                r: false,
                u: false,
                d: undefined,
            },
        },
        [PaymentMethod.classInternalName]: {
            operations: {
                c: undefined,
                r: false,
                u: undefined,
                d: undefined,
            },
        },
        [PhoneCheck.classInternalName]: {
            operations: {
                c: undefined,
                r: false,
                u: undefined,
                d: undefined,
            },
        },
        [Policy.classInternalName]: {
            operations: {
                c: false,
                r: false,
                u: false,
                d: undefined,
            },
        },
        [RewardAccount.classInternalName]: {
            operations: {
                c: undefined,
                r: false,
                u: false,
                d: undefined,
            },
        },
        [RewardTransaction.classInternalName]: {
            operations: {
                c: false,
                r: false,
                u: undefined,
                d: undefined,
            },
        },
        [Transaction.classInternalName]: {
            operations: {
                c: undefined,
                r: false,
                u: undefined,
                d: undefined,
            },
        },
        [User.classInternalName]: {
            operations: {
                c: undefined,
                r: false,
                u: false,
                d: false,
            },
        },
        [AuthSession.classInternalName]: {
            operations: {
                c: undefined,
                r: false,
                u: false,
                d: undefined,
            },
        },
        [OrgReview.classInternalName]: {
            operations: {
                c: undefined,
                r: false,
                u: false,
                d: undefined,
            },
        },
    };
}

//
//
// PANEL REGISTRY
//
//

// A container for all entity and permission actions
export interface PanelRegistry {
    entityActions: RegistryEntityActionCollection;
}

// A registry which allows all actions currently available in the admin panel
export class SuperRegistry implements PanelRegistry {
    constructor() {
        this.entityActions = getAllRegistryEntityActions();
    }
}

// A registry which filters out actions available in a policy
export class PolicyBasedRegistry implements PanelRegistry {
    constructor(policy: ?Policy) {
        // Transform what's in the policy into registry records

        this.entityActions = getAllDeniedEntityActions();

        // Get the entity action records
        const peas = policy.record[Policy.fields.entityActions.recordName].map((r) => new PolicyEntityAction(r));

        peas.forEach((action) => {
            // Get the entity type for faster access
            const entityType = action.record[PolicyEntityAction.fields.entityType.recordName];

            // Just a safety check in case the backoffice
            // doesn't get updated in time with the backend entity types
            if (this.entityActions.hasOwnProperty(entityType)) {
                // Grab the registry counterpart of the policy action
                const rea = this.entityActions[entityType];

                // Project the operations
                const peaOperations = action.record[PolicyEntityAction.fields.operations.recordName];

                rea.operations.c = rea.operations.c === undefined ? undefined : peaOperations.includes(1);
                rea.operations.r = rea.operations.r === undefined ? undefined : peaOperations.includes(2);
                rea.operations.u = rea.operations.u === undefined ? undefined : peaOperations.includes(3);
                rea.operations.d = rea.operations.d === undefined ? undefined : peaOperations.includes(4);
            }
        });
    }
}
